import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import RichTextRenderer from '../common/RichTextRenderer';

const easing = [0.22, 1, 0.36, 1];

const containerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: easing, staggerChildren: 0.1, delayChildren: 0.55 }
  },
  exit: { opacity: 0, y: -12, transition: { duration: 0.25, ease: easing } }
};

const childVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easing } }
};

/**
 * LessonContentView — editorial body for the lesson.
 * Article container is generous (max-w-3xl); the shadow CSS constrains
 * text to a 62ch measure while letting media breach to the full width.
 */
const LessonContentView = ({ isViewingBackwards, getDisplayedContentLocal }) => {
  const displayed = getDisplayedContentLocal();
  const contentKey = displayed?._id || displayed?.title || 'lesson';

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeContent = isMobile && displayed.mobileContent
    ? displayed.mobileContent
    : displayed.content;

  return (
    <article className="max-w-5xl mx-auto">
      <AnimatePresence mode="wait">
        <motion.div
          key={contentKey}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {isViewingBackwards && (
            <motion.div
              variants={childVariants}
              className="mb-8 max-w-[64ch] pl-4 border-l-2 border-foreground/30"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-foreground/55 mb-1">
                Reviewing
              </div>
              <h4 className="text-sm font-semibold text-foreground leading-snug mb-0.5">Reviewing Previous Lesson</h4>
              <p className="text-sm text-foreground/70 leading-snug">
                You are viewing a previous lesson.
              </p>
            </motion.div>
          )}
          <motion.div variants={childVariants}>
            <RichTextRenderer
              content={activeContent}
              className="lesson-content"
              fontSize="17px"
              lineHeight="1.7"
              maxWidth="none"
              allowInlineStyles={true}
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </article>
  );
};

export default LessonContentView;
